import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackagePlus, Plus } from 'lucide-react';
import { formatMoney, formatQty, PAYMENT_METHODS, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import type { ProductDto, Supplier } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { MoneyInput, Tabs } from '../../ui/inputs';
import { LinesEditor, linesReady, newLine, type DocLine } from '../stock/LinesEditor';

interface PoRow {
  id: string;
  number: string;
  status: string;
  expectedDate: string | null;
  createdAt: string;
  supplierId: string;
  supplierName: string;
  branchName: string;
  totalCents: number;
  lineCount: number;
}
interface PoDetail {
  id: string;
  number: string;
  status: string;
  supplierId: string;
  supplierName: string;
  branchId: string;
  branchName: string;
  notes: string | null;
  lines: { id: string; productId: string; productName: string; sku: string; unit: string; qtyMilli: number; unitCostCents: number; receivedMilli: number; tracksExpiry: boolean }[];
  receipts: { id: string; number: string; createdAt: string }[];
}
interface GrnRow {
  id: string;
  number: string;
  createdAt: string;
  supplierName: string;
  branchName: string;
  supplierInvoiceRef: string | null;
  linesCostCents: number;
  invoiceTotalCents: number;
  onCredit: boolean;
  receivedByName: string | null;
}

const STATUS: Record<string, { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' | 'gold' }> = {
  ordered: { label: 'Ordered', tone: 'gold' },
  partially_received: { label: 'Part received', tone: 'warn' },
  received: { label: 'Received', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function PurchasingPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [tab, setTab] = useState<'orders' | 'deliveries'>(can('purchases.manage') ? 'orders' : 'deliveries');
  const [newOrder, setNewOrder] = useState(false);
  const [receiving, setReceiving] = useState<{ poId?: string } | null>(null);
  const [openPo, setOpenPo] = useState<string | null>(null);
  const orders = useQuery({ queryKey: ['purchase-orders'], queryFn: () => api.get<PoRow[]>('/api/purchase-orders'), enabled: tab === 'orders' });
  const grns = useQuery({ queryKey: ['goods-receipts'], queryFn: () => api.get<GrnRow[]>('/api/goods-receipts'), enabled: tab === 'deliveries' });

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="Order from suppliers and record deliveries as they arrive."
        actions={
          <>
            {can('purchases.manage') && <Button icon={<Plus className="size-4" />} onClick={() => setNewOrder(true)}>New order</Button>}
            {can('stock.receive') && <Button variant="primary" icon={<PackagePlus className="size-4" />} onClick={() => setReceiving({})}>Receive delivery</Button>}
          </>
        }
      />
      <Tabs value={tab} onChange={setTab} tabs={[{ value: 'orders', label: 'Purchase orders' }, { value: 'deliveries', label: 'Deliveries received' }]} />
      {tab === 'orders' && (
        <Panel>
          {orders.isLoading && <Spinner />}
          {orders.error && <div className="p-4"><ErrorNotice error={orders.error} /></div>}
          {orders.data?.length === 0 && <EmptyState title="No purchase orders yet">Create an order to send to a supplier, then receive the delivery against it.</EmptyState>}
          {!!orders.data?.length && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2.5 font-medium">Order</th>
                    <th className="px-3 py-2.5 font-medium">Supplier</th>
                    <th className="px-3 py-2.5 font-medium">Branch</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-5 py-2.5 text-right font-medium">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {orders.data.map((o) => (
                    <tr key={o.id} className="hover:bg-ledger-50/60">
                      <td className="px-5 py-3">
                        <button className="font-medium text-ledger-700 hover:underline" onClick={() => setOpenPo(o.id)}>{o.number}</button>
                        <span className="block text-xs text-muted">{formatDateTime(o.createdAt)}</span>
                      </td>
                      <td className="px-3 py-3">{o.supplierName}</td>
                      <td className="px-3 py-3 text-sm">{o.branchName}</td>
                      <td className="px-3 py-3"><Badge tone={STATUS[o.status]?.tone}>{STATUS[o.status]?.label ?? o.status}</Badge></td>
                      <td className="px-5 py-3 text-right num">{formatMoney(o.totalCents, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
      {tab === 'deliveries' && (
        <Panel>
          {grns.isLoading && <Spinner />}
          {grns.error && <div className="p-4"><ErrorNotice error={grns.error} /></div>}
          {grns.data?.length === 0 && <EmptyState title="No deliveries recorded yet" />}
          {!!grns.data?.length && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2.5 font-medium">Delivery</th>
                    <th className="px-3 py-2.5 font-medium">Supplier</th>
                    <th className="px-3 py-2.5 font-medium">Invoice</th>
                    <th className="px-3 py-2.5 font-medium">Received by</th>
                    <th className="px-5 py-2.5 text-right font-medium">Invoice total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {grns.data.map((g) => (
                    <tr key={g.id}>
                      <td className="px-5 py-3">
                        <span className="font-medium">{g.number}</span>
                        <span className="block text-xs text-muted">{formatDateTime(g.createdAt)} — {g.branchName}</span>
                      </td>
                      <td className="px-3 py-3">{g.supplierName}</td>
                      <td className="px-3 py-3 text-sm">
                        {g.supplierInvoiceRef ?? '—'} {g.onCredit && <Badge tone="gold">On account</Badge>}
                      </td>
                      <td className="px-3 py-3 text-sm">{g.receivedByName ?? '—'}</td>
                      <td className="px-5 py-3 text-right num">{formatMoney(g.invoiceTotalCents, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
      {newOrder && <OrderDialog onClose={() => setNewOrder(false)} />}
      {receiving && <ReceiveDialog poId={receiving.poId} onClose={() => setReceiving(null)} />}
      {openPo && (
        <PoDialog
          id={openPo}
          onClose={() => setOpenPo(null)}
          onReceive={() => {
            setReceiving({ poId: openPo });
            setOpenPo(null);
          }}
        />
      )}
    </>
  );
}

function useSuppliers() {
  return useQuery({ queryKey: ['suppliers', false], queryFn: () => api.get<Supplier[]>('/api/suppliers') });
}

function OrderDialog({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const qc = useQueryClient();
  const toast = useToast();
  const suppliers = useSuppliers();
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [expectedDate, setExpected] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DocLine[]>([]);
  const m = useMutation({
    mutationFn: () =>
      api.post<{ number: string }>('/api/purchase-orders', {
        supplierId,
        branchId,
        expectedDate: expectedDate || null,
        notes,
        lines: lines.map((l) => ({ productId: l.product.id, qtyMilli: l.qtyMilli, unitCostCents: l.unitCostCents ?? 0 })),
      }),
    onSuccess: (po) => {
      toast(`Order ${po.number} created`);
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="New purchase order"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!supplierId || !linesReady(lines, true)} busy={m.isPending} onClick={() => m.mutate()}>Create order</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Field label="Supplier" htmlFor="po-supplier" error={f.supplierId}>
          <Select id="po-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Choose…</option>
            {suppliers.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Deliver to" htmlFor="po-branch">
          <Select id="po-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Expected" htmlFor="po-date">
          <TextInput id="po-date" type="date" value={expectedDate} onChange={(e) => setExpected(e.target.value)} />
        </Field>
      </div>
      <LinesEditor lines={lines} onChange={setLines} branchId={branchId} showCost qtyLabel="Order qty" />
      <Field label="Notes for the supplier" htmlFor="po-notes" className="mt-4">
        <TextInput id="po-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function PoDialog({ id, onClose, onReceive }: { id: string; onClose: () => void; onReceive: () => void }) {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['purchase-order', id], queryFn: () => api.get<PoDetail>(`/api/purchase-orders/${id}`) });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/purchase-orders/${id}/cancel`),
    onSuccess: () => {
      toast('Order cancelled');
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      onClose();
    },
  });
  const po = q.data;
  const open = po && (po.status === 'ordered' || po.status === 'partially_received');
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={po ? `Order ${po.number}` : 'Order'}
      description={po ? `${po.supplierName} → ${po.branchName}` : undefined}
      footer={
        <>
          {open && can('purchases.manage') && (
            <Button variant="ghost" className="mr-auto text-danger" busy={cancel.isPending} onClick={() => cancel.mutate()}>
              Cancel order
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
          {open && can('stock.receive') && <Button variant="primary" onClick={onReceive}>Receive delivery</Button>}
        </>
      }
    >
      {q.isLoading && <Spinner />}
      {cancel.error && <FormError error={cancel.error} />}
      {po && (
        <>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line text-sm text-muted">
                <th className="py-2 font-medium">Product</th>
                <th className="py-2 text-right font-medium">Ordered</th>
                <th className="py-2 text-right font-medium">Received</th>
                <th className="py-2 text-right font-medium">Unit cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {po.lines.map((l) => (
                <tr key={l.id}>
                  <td className="py-2">{l.productName}</td>
                  <td className="py-2 text-right num">{formatQty(l.qtyMilli)}</td>
                  <td className="py-2 text-right num">{formatQty(l.receivedMilli)}</td>
                  <td className="py-2 text-right num">{formatMoney(l.unitCostCents, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {po.notes && <p className="mt-3 text-sm text-muted">Notes: {po.notes}</p>}
          {po.receipts.length > 0 && <p className="mt-3 text-sm text-muted">Deliveries: {po.receipts.map((r) => r.number).join(', ')}</p>}
        </>
      )}
    </Dialog>
  );
}

function ReceiveDialog({ poId, onClose }: { poId?: string; onClose: () => void }) {
  const { me, hasModule } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const suppliers = useSuppliers();
  const po = useQuery({ queryKey: ['purchase-order', poId], queryFn: () => api.get<PoDetail>(`/api/purchase-orders/${poId}`), enabled: !!poId });
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState(branch?.id ?? '');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState<number | null>(null);
  const [onCredit, setOnCredit] = useState(true);
  const [paidMethod, setPaidMethod] = useState('cash');
  const [lines, setLines] = useState<DocLine[] | null>(poId ? null : []);

  // Prefill from the purchase order: outstanding quantities at ordered cost.
  if (poId && po.data && lines === null) {
    setSupplierId(po.data.supplierId);
    setBranchId(po.data.branchId);
    setLines(
      po.data.lines
        .filter((l) => l.qtyMilli > l.receivedMilli)
        .map((l) =>
          newLine({ id: l.productId, name: l.productName, sku: l.sku, unit: l.unit, tracksExpiry: l.tracksExpiry } as ProductDto, {
            qtyMilli: l.qtyMilli - l.receivedMilli,
            unitCostCents: l.unitCostCents,
            purchaseOrderLineId: l.id,
          }),
        ),
    );
  }
  const list = lines ?? [];
  const linesCost = list.reduce((a, l) => a + (l.qtyMilli && l.unitCostCents != null ? Math.round((l.unitCostCents * l.qtyMilli) / 1000) : 0), 0);
  const m = useMutation({
    mutationFn: () =>
      api.post<{ number: string }>('/api/goods-receipts', {
        supplierId,
        branchId,
        purchaseOrderId: poId ?? null,
        supplierInvoiceRef: invoiceRef,
        invoiceTotalCents: invoiceTotal,
        onCredit,
        paidMethod: onCredit ? null : paidMethod,
        lines: list.map((l) => ({
          productId: l.product.id,
          purchaseOrderLineId: l.purchaseOrderLineId ?? null,
          qtyMilli: l.qtyMilli,
          unitCostCents: l.unitCostCents ?? 0,
          batchNo: l.batchNo || null,
          expiryDate: l.expiryDate || null,
        })),
      }),
    onSuccess: (gr) => {
      toast(`Delivery ${gr.number} received into stock`);
      for (const k of ['goods-receipts', 'purchase-orders', 'products', 'purchase-order']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={poId && po.data ? `Receive delivery for ${po.data.number}` : 'Receive delivery'}
      description="Count what actually arrived. Stock goes up as soon as you save."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!supplierId || !linesReady(list, true)} busy={m.isPending} onClick={() => m.mutate()}>
            Receive into stock
          </Button>
        </>
      }
    >
      <FormError error={m.error} />
      {poId && po.isLoading && <Spinner />}
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Field label="Supplier" htmlFor="gr-supplier" error={f.supplierId}>
          <Select id="gr-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} disabled={!!poId}>
            <option value="">Choose…</option>
            {suppliers.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
        <Field label="Branch" htmlFor="gr-branch">
          <Select id="gr-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={!!poId}>
            {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Supplier invoice number" htmlFor="gr-inv" error={f.supplierInvoiceRef}>
          <TextInput id="gr-inv" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} />
        </Field>
      </div>
      <LinesEditor lines={list} onChange={setLines} branchId={branchId} showCost showExpiry={hasModule('expiry')} qtyLabel="Received" allowAdd={!poId} />
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field label="Invoice total (incl. VAT)" htmlFor="gr-total" hint={`Leave blank to use ${formatMoney(linesCost, cur)}`}>
          <MoneyInput id="gr-total" currency={cur} value={invoiceTotal} onChange={setInvoiceTotal} />
        </Field>
        <div className="sm:col-span-2 sm:pt-7">
          <Checkbox label="Pay later (add to what we owe this supplier)" checked={onCredit} onChange={setOnCredit} />
          {!onCredit && (
            <Select aria-label="Paid by" className="mt-2 max-w-60" value={paidMethod} onChange={(e) => setPaidMethod(e.target.value)}>
              {(['cash', 'bank_transfer', 'ecocash', 'card'] as const).map((k) => <option key={k} value={k}>Paid by {PAYMENT_METHODS[k].label.split(' (')[0]}</option>)}
            </Select>
          )}
        </div>
      </div>
    </Dialog>
  );
}
